// src/lib/travelHealth/pgds/pgdIds.ts
//
// PGD IDs for every selectable travel health product. Defined once here and
// imported everywhere else — no magic strings.

export const PGD_IDS = {
  HEP_A: "pgd-hep-a",
  HEP_B: "pgd-hep-b",
  HEP_AB: "pgd-hep-ab",
  TYPHOID: "pgd-typhoid",
  CHOLERA: "pgd-cholera",
  MENACWY: "pgd-menacwy",
  DTP_REVAXIS: "pgd-dtp-revaxis",
  JAPANESE_ENC: "pgd-japanese-enc",
  RABIES: "pgd-rabies",
  TBE: "pgd-tbe",
  CHIKUNGUNYA: "pgd-chikungunya",
  DENGUE: "pgd-dengue",
  YELLOW_FEVER: "pgd-yellow-fever",
  MALARONE: "pgd-malarone",
  MEFLOQUINE: "pgd-mefloquine",
  DOXYCYCLINE: "pgd-doxycycline",
  ACETAZOLAMIDE: "pgd-acetazolamide",
  MELATONIN: "pgd-melatonin",
  NORETHISTERONE: "pgd-norethisterone",
  RIFAXIMIN: "pgd-rifaximin",
} as const;

export type PgdId = (typeof PGD_IDS)[keyof typeof PGD_IDS];

export interface ProductMeta {
  pgdId: PgdId;
  displayName: string;
  isInjectedLiveVaccine: boolean;
  isOralLiveVaccine: boolean;
  isVaccine: boolean;
}

export const PRODUCT_META: Record<PgdId, ProductMeta> = {
  [PGD_IDS.HEP_A]: {
    pgdId: PGD_IDS.HEP_A,
    displayName: "Hepatitis A",
    isInjectedLiveVaccine: false,
    isOralLiveVaccine: false,
    isVaccine: true,
  },
  [PGD_IDS.HEP_B]: {
    pgdId: PGD_IDS.HEP_B,
    displayName: "Hepatitis B",
    isInjectedLiveVaccine: false,
    isOralLiveVaccine: false,
    isVaccine: true,
  },
  [PGD_IDS.HEP_AB]: {
    pgdId: PGD_IDS.HEP_AB,
    displayName: "Combined Hepatitis A and B",
    isInjectedLiveVaccine: false,
    isOralLiveVaccine: false,
    isVaccine: true,
  },
  [PGD_IDS.TYPHOID]: {
    pgdId: PGD_IDS.TYPHOID,
    displayName: "Typhoid",
    isInjectedLiveVaccine: false,
    isOralLiveVaccine: true,
    isVaccine: true,
  },
  [PGD_IDS.CHOLERA]: {
    pgdId: PGD_IDS.CHOLERA,
    displayName: "Oral cholera",
    isInjectedLiveVaccine: false,
    isOralLiveVaccine: true,
    isVaccine: true,
  },
  [PGD_IDS.MENACWY]: {
    pgdId: PGD_IDS.MENACWY,
    displayName: "MenACWY",
    isInjectedLiveVaccine: false,
    isOralLiveVaccine: false,
    isVaccine: true,
  },
  [PGD_IDS.DTP_REVAXIS]: {
    pgdId: PGD_IDS.DTP_REVAXIS,
    displayName: "DTP booster (Revaxis)",
    isInjectedLiveVaccine: false,
    isOralLiveVaccine: false,
    isVaccine: true,
  },
  [PGD_IDS.JAPANESE_ENC]: {
    pgdId: PGD_IDS.JAPANESE_ENC,
    displayName: "Japanese encephalitis",
    isInjectedLiveVaccine: false,
    isOralLiveVaccine: false,
    isVaccine: true,
  },
  [PGD_IDS.RABIES]: {
    pgdId: PGD_IDS.RABIES,
    displayName: "Rabies pre-exposure",
    isInjectedLiveVaccine: false,
    isOralLiveVaccine: false,
    isVaccine: true,
  },
  [PGD_IDS.TBE]: {
    pgdId: PGD_IDS.TBE,
    displayName: "Tick-borne encephalitis",
    isInjectedLiveVaccine: false,
    isOralLiveVaccine: false,
    isVaccine: true,
  },
  [PGD_IDS.CHIKUNGUNYA]: {
    pgdId: PGD_IDS.CHIKUNGUNYA,
    displayName: "Chikungunya (VIMKUNYA)",
    isInjectedLiveVaccine: false,
    isOralLiveVaccine: false,
    isVaccine: true,
  },
  [PGD_IDS.DENGUE]: {
    pgdId: PGD_IDS.DENGUE,
    displayName: "Dengue (Qdenga)",
    isInjectedLiveVaccine: true,
    isOralLiveVaccine: false,
    isVaccine: true,
  },
  [PGD_IDS.YELLOW_FEVER]: {
    pgdId: PGD_IDS.YELLOW_FEVER,
    displayName: "Yellow fever (STAMARIL)",
    isInjectedLiveVaccine: true,
    isOralLiveVaccine: false,
    isVaccine: true,
  },
  [PGD_IDS.MALARONE]: {
    pgdId: PGD_IDS.MALARONE,
    displayName: "Malarone (atovaquone/proguanil)",
    isInjectedLiveVaccine: false,
    isOralLiveVaccine: false,
    isVaccine: false,
  },
  [PGD_IDS.MEFLOQUINE]: {
    pgdId: PGD_IDS.MEFLOQUINE,
    displayName: "Mefloquine (Lariam)",
    isInjectedLiveVaccine: false,
    isOralLiveVaccine: false,
    isVaccine: false,
  },
  [PGD_IDS.DOXYCYCLINE]: {
    pgdId: PGD_IDS.DOXYCYCLINE,
    displayName: "Doxycycline",
    isInjectedLiveVaccine: false,
    isOralLiveVaccine: false,
    isVaccine: false,
  },
  [PGD_IDS.ACETAZOLAMIDE]: {
    pgdId: PGD_IDS.ACETAZOLAMIDE,
    displayName: "Acetazolamide",
    isInjectedLiveVaccine: false,
    isOralLiveVaccine: false,
    isVaccine: false,
  },
  [PGD_IDS.MELATONIN]: {
    pgdId: PGD_IDS.MELATONIN,
    displayName: "Melatonin",
    isInjectedLiveVaccine: false,
    isOralLiveVaccine: false,
    isVaccine: false,
  },
  [PGD_IDS.NORETHISTERONE]: {
    pgdId: PGD_IDS.NORETHISTERONE,
    displayName: "Norethisterone",
    isInjectedLiveVaccine: false,
    isOralLiveVaccine: false,
    isVaccine: false,
  },
  [PGD_IDS.RIFAXIMIN]: {
    pgdId: PGD_IDS.RIFAXIMIN,
    displayName: "Rifaximin (Xifaxanta)",
    isInjectedLiveVaccine: false,
    isOralLiveVaccine: false,
    isVaccine: false,
  },
} as const;
